import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Phone, Plus, Loader2 } from 'lucide-react';
import {
  ClientFilters, useClientSearch, Pager, StatusPill, EmptyMsg, showDay,
} from './salesShared';
import ActivityModal from './ActivityModal';
import ClientTimelineModal from './ClientTimelineModal';

const SORTS = [
  { value: 'last_contact', label: 'Never / longest ago first' },
  { value: 'follow_up', label: 'Next follow-up first' },
  { value: 'name', label: 'Name A–Z' },
];

// A work queue: filter to a segment, then go down the list. "Log" records
// the result of a call; "Save & next" opens the next client straight away.
const CallListTab = ({ salesReps, facets, defaultRep, onChanged }) => {
  const { user } = useAuth();
  const isSalesRep = user?.role === 'sales_rep';
  const [filters, setFilters] = useState({ q: '', rep: defaultRep, industry: 'all', status: 'all', postal: '', has_phone: true });
  const [sort, setSort] = useState('last_contact');
  const search = useClientSearch(filters, { pageSize: 50, sort });
  const [logFor, setLogFor] = useState(null);
  const [viewId, setViewId] = useState(null);

  // The admin's rep picker in the Sales header drives this list too.
  useEffect(() => { setFilters(f => ({ ...f, rep: defaultRep })); }, [defaultRep]);

  const afterSave = async () => {
    await search.reload();
    if (onChanged) onChanged();
  };

  // After "Save & next": the client that followed the one just logged.
  const openNext = (current) => {
    const idx = search.items.findIndex(c => c.id === current.id);
    const next = idx >= 0 ? search.items[idx + 1] : null;
    setLogFor(next || null);
  };

  return (
    <div className="space-y-3" data-testid="call-list-tab">
      <ClientFilters filters={filters} onChange={setFilters} facets={facets} salesReps={salesReps} isSalesRep={isSalesRep} />
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-[#94A3B8]" data-testid="call-list-count">
          {search.loading ? 'Loading…' : `${search.total.toLocaleString()} client(s) on this list`}
        </p>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] h-9 text-sm w-[240px]" data-testid="call-list-sort"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#121212] border-[#1E293B]">
            {SORTS.map(s => <SelectItem key={s.value} value={s.value} className="text-[#F8FAFC]">{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {search.error && <div className="text-sm text-red-400">{search.error}</div>}

      <div className="bg-[#121212] border border-[#1E293B] rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="call-list-table">
          <thead className="bg-[#0A0A0A] text-[#94A3B8] text-xs uppercase">
            <tr>
              <th className="text-left px-3 py-2">Client</th>
              <th className="text-left px-3 py-2">Phone</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Last contact</th>
              <th className="text-left px-3 py-2">Next follow-up</th>
              <th className="text-right px-3 py-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {search.items.map(c => (
              <tr key={c.id} className="border-t border-[#1E293B] hover:bg-[#0A0A0A]/60" data-testid={`call-row-${c.id}`}>
                <td className="px-3 py-2">
                  <button type="button" onClick={() => setViewId(c.id)} className="text-left text-[#F8FAFC] hover:text-[#3B82F6]" data-testid={`call-open-${c.id}`}>
                    {c.business_name}
                  </button>
                  <div className="text-xs text-[#475569]">{[c.industry, c.contact_name].filter(Boolean).join(' · ')}</div>
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {c.phone
                    ? <a href={`tel:${c.phone}`} className="text-[#3B82F6] inline-flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</a>
                    : <span className="text-[#475569]">—</span>}
                </td>
                <td className="px-3 py-2"><StatusPill status={c.sales_status} /></td>
                <td className="px-3 py-2 text-[#94A3B8] whitespace-nowrap">{c.last_contact_date ? showDay(c.last_contact_date) : 'never'}</td>
                <td className="px-3 py-2 text-[#94A3B8] whitespace-nowrap">{showDay(c.next_follow_up_date) || '—'}</td>
                <td className="px-3 py-2 text-right">
                  <Button size="sm" onClick={() => setLogFor(c)} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white h-7 text-xs" data-testid={`call-log-${c.id}`}>
                    <Plus className="w-3 h-3 mr-1" /> Log
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {search.loading && search.items.length === 0 && <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#3B82F6]" /></div>}
        {!search.loading && search.items.length === 0 && <EmptyMsg text="No clients match these filters." />}
      </div>

      <Pager page={search.page} pages={search.pages} total={search.total} pageSize={search.pageSize} onPage={search.setPage} testid="call-list-pager" />

      <ActivityModal
        open={!!logFor}
        client={logFor}
        onClose={() => setLogFor(null)}
        onSaved={afterSave}
        onSaveNext={openNext}
      />
      <ClientTimelineModal
        clientId={viewId}
        open={!!viewId}
        onClose={() => setViewId(null)}
        onLog={(c) => { setViewId(null); setLogFor(c); }}
        onChanged={afterSave}
      />
    </div>
  );
};

export default CallListTab;
